/**
 * useNoticePopups — asks the server for pop-ups every 30 s while Studio is in
 * front and the facilitator isn't in the drill console (lib/notices), and
 * shows them one at a time in Design's order (urgent first, 3+ within 10 s
 * folded). Marks each shown, so it never pops again on any device.
 *
 * Off (`enabled` false) until Studio's bell and list exist: a pop-up that
 * fades after 6 s must have a list to wait in (Tools to Design, 12 Oct).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationApi } from '@/lib/api';
import { NOTICE_POLL_MS, nextPopup, shouldAskForPopups, type Notice, type PopupStep } from '@/lib/notices';

export function useNoticePopups(enabled: boolean, inConsole: boolean) {
  const [queue, setQueue] = useState<Notice[]>([]);
  const [step, setStep] = useState<PopupStep | null>(null);
  const seen = useRef(new Set<string>());

  const ask = useCallback(async () => {
    if (!shouldAskForPopups({ visible: document.visibilityState === 'visible', inConsole })) return;
    try {
      const fresh = (await notificationApi.pendingPopups()).filter((n) => !seen.current.has(n.id));
      if (fresh.length === 0) return;
      fresh.forEach((n) => seen.current.add(n.id));
      setQueue((q) => [...q, ...fresh]);
    } catch {
      // Not live yet, or offline: nothing pops, and the next check tries again.
    }
  }, [inConsole]);

  useEffect(() => {
    if (!enabled) return;
    void ask();
    const timer = window.setInterval(() => void ask(), NOTICE_POLL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') void ask(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onFocus); };
  }, [enabled, ask]);

  // The next card, once the current one is gone; never while in the console.
  useEffect(() => {
    if (step || inConsole || queue.length === 0) return;
    const next = nextPopup(queue);
    if (!next) { setQueue([]); return; }
    setStep(next);
    const ids = next.kind === 'one' ? [next.notice.id] : next.ids;
    // Shown once means shown everywhere. If the mark fails, this tab still won't
    // pop it again (it's in `seen`); another device may.
    ids.forEach((id) => { void notificationApi.markShown(id).catch(() => undefined); });
  }, [step, inConsole, queue]);

  const folded = step?.kind === 'many' ? queue.filter((n) => step.ids.includes(n.id)) : [];

  const dismiss = useCallback(() => {
    setStep((s) => {
      if (s) {
        const ids = s.kind === 'one' ? [s.notice.id] : s.ids;
        setQueue((q) => q.filter((n) => !ids.includes(n.id)));
      }
      return null;
    });
  }, []);

  return { step, folded, dismiss };
}
